import { createContext, Dispatch, SetStateAction } from 'react';
import { SubmitAnswerParams } from 'src/hooks/useSubmitAnswer';
import {
    PrefillById,
    QuestionList,
    QuestionRecord,
    QuizRecord,
    SectionRecord,
} from 'src/types/Quiz';

const noop = () => {
    // No operation
};

export type QuizContextType = {
    quizToken?: string;
    quizFlow: string;
    quizType: string;
    quizName?: string;
    quizVersion?: string;
    quizSubVersion?: string;
    quizResumeSource?: string;
    iterationOfProgressBar?: 'percentage' | 'fraction';
    currentQuestionId: string | null;
    currentQuestion: QuestionRecord;
    currentQuestionIndex: number;
    lastCompletedQuestionIndex: number;
    questionById: { [key: string]: QuestionRecord };
    questionList: QuestionList;
    completedQuestionList: QuestionList;
    prefillById: PrefillById;
    prefillQuestionIds: string[];
    sectionById: { [key: string]: string };
    sectionList: SectionRecord[];
    medications: string[];
    plans: string[];
    submittedQuestionIds: string[];
    inQueue: boolean;
    handleSubmitAnswer: (
        answer: SubmitAnswerParams,
        callback?: (response: QuizRecord) => void,
        options?: { signal: AbortSignal },
    ) => void;
    handleNavigateToQuestion: (questionId?: string | null) => void;
    handleNavigatePrevQuestion: () => void;
    handleNavigateNextQuestion: () => void;
    handleSetQueueQuestions: (
        questionId: string,
        sectionId: number | string,
        unansweredOnly: boolean,
    ) => void;
    state: QuizState;
};

type QuizState = {
    pageTransitioning: [boolean, Dispatch<SetStateAction<boolean>>];
    pageBottomInView: [boolean, Dispatch<SetStateAction<boolean>>];
    pageRendered: [boolean, Dispatch<SetStateAction<boolean>>];
    isLongContent: [boolean, Dispatch<SetStateAction<boolean>>];
    isSmallContent: [boolean, Dispatch<SetStateAction<boolean>>];
    isOverflowing: [boolean, Dispatch<SetStateAction<boolean>>];
    backDisabled: [boolean, Dispatch<SetStateAction<boolean>>];
    backVisible: [boolean, Dispatch<SetStateAction<boolean>>];
    forwardDisabled: [boolean, Dispatch<SetStateAction<boolean>>];
    forwardVisible: [boolean, Dispatch<SetStateAction<boolean>>];
    continueText: [string | null, Dispatch<SetStateAction<string | null>>];
    continueDisabled: [boolean, Dispatch<SetStateAction<boolean>>];
    continueVisible: [boolean, Dispatch<SetStateAction<boolean>>];
    acceptVisible: [boolean, Dispatch<SetStateAction<boolean>>];
    loading: [boolean, Dispatch<SetStateAction<boolean>>];
};

export const initialState: QuizContextType = {
    quizToken: '',
    quizFlow: '',
    quizType: '',
    quizName: '',
    quizVersion: '',
    quizSubVersion: '',
    quizResumeSource: '',
    currentQuestionId: null,
    currentQuestion: {
        sectionId: '',
        questionId: '',
        questionType: '',
        questionText: '',
        answerList: [],
        userAnswers: [],
        prefillAnswers: [],
    },
    currentQuestionIndex: 0,
    lastCompletedQuestionIndex: 0,
    questionById: {},
    questionList: [],
    completedQuestionList: [],
    prefillById: {},
    prefillQuestionIds: [],
    sectionById: {},
    sectionList: [],
    medications: [],
    plans: [],
    submittedQuestionIds: [],
    inQueue: false,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    handleSubmitAnswer: (_answer: SubmitAnswerParams) => {},
    handleNavigateToQuestion: noop,
    handleNavigatePrevQuestion: noop,
    handleNavigateNextQuestion: noop,
    handleSetQueueQuestions: noop,
    state: {
        pageTransitioning: [false, noop],
        pageBottomInView: [false, noop],
        pageRendered: [false, noop],
        isLongContent: [false, noop],
        isSmallContent: [false, noop],
        isOverflowing: [false, noop],
        backDisabled: [false, noop],
        backVisible: [false, noop],
        forwardDisabled: [false, noop],
        forwardVisible: [false, noop],
        continueText: [null, noop],
        continueDisabled: [false, noop],
        continueVisible: [false, noop],
        acceptVisible: [false, noop],
        loading: [false, noop],
    },
};

export const QuizContext = createContext<QuizContextType>(initialState);
